import { isNull } from "lodash";
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  ControlPosition,
  GoogleMapsContext,
  latLngEquals,
  Map as GoogleMap,
  MapControl,
  useMap,
} from "@vis.gl/react-google-maps";

import { UndoRedoControl } from "./undo-redo-control.tsx";
import { useDrawingManager } from "./use-drawing-manager.tsx";

const useCircle = (props) => {
  const { onRadiusChanged, onCenterChanged, radius, center, ...circleOptions } =
    props;

  const callbacks = useRef({});
  Object.assign(callbacks.current, {
    onRadiusChanged,
    onCenterChanged,
  });

  const circle = useRef(new window.google.maps.Circle()).current;
  circle.setOptions(circleOptions);

  useEffect(() => {
    if (!center) return;
    if (!latLngEquals(center, circle.getCenter())) circle.setCenter(center);
  }, [center]);

  useEffect(() => {
    if (radius === undefined || radius === null) return;
    if (radius !== circle.getRadius()) circle.setRadius(radius);
  }, [radius]);

  const map = useContext(GoogleMapsContext)?.map;

  // create circle instance and add to the map once the map is available
  useEffect(() => {
    if (!map) {
      if (map === undefined)
        console.error("<Circle> has to be inside a Map component.");

      return;
    }

    circle.setMap(map);

    return () => {
      circle.setMap(null);
    };
  }, [map]);

  // attach and re-attach event-handlers when any of the properties change
  useEffect(() => {
    if (!circle) return;

    // Add event listeners
    const gme = window.google.maps.event;
    gme.addListener(circle, "radius_changed", () => {
      const newRadius = circle.getRadius();
      callbacks.current.onRadiusChanged?.(newRadius);
    });
    gme.addListener(circle, "center_changed", () => {
      const newCenter = circle.getCenter();
      callbacks.current.onCenterChanged?.(newCenter);
    });

    return () => {
      gme.clearInstanceListeners(circle);
    };
  }, [circle]);

  return circle;
};

const Circle = forwardRef((props, ref) => {
  const circle = useCircle(props);

  useImperativeHandle(ref, () => circle);

  return null;
});

const mapStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = {
  lat: 39.8283, // Center of the continental US
  lng: -98.5795,
};

const mapOptions = {
  clickableIcons: false,
  // colorScheme: "DARK",
  fullscreenControl: false,
  mapTypeControl: false,
  maxZoom: 22,
  minZoom: 0,
  streetViewControl: false,
  styles: [
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "landscape",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "off" }],
    },
  ],
  zoomControl: false,
};

const Map = ({
  category,
  // previewCenter,
  // previewRadius,
  // selectedLocation,
  handleChangeShapeData,
  // usStates,
}) => {
  const map = useMap();
  const drawingManager = useDrawingManager(category);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(3);

  // useEffect(() => {
  //   if (!isNull(selectedLocation)) {
  //     if (
  //       selectedLocation.type === "distance" &&
  //       selectedLocation.lat &&
  //       selectedLocation.lon
  //     ) {
  //       setMapCenter(null);
  //       setMapZoom(null);

  //       let circle = new window.google.maps.Circle();
  //       circle.setRadius(previewRadius);
  //       circle.setCenter(previewCenter);

  //       let bounds = new window.google.maps.LatLngBounds();
  //       bounds.union(circle.getBounds());
  //       map?.fitBounds(bounds, 20);
  //     } else if (selectedLocation.type === "state" && selectedLocation.state && usStates.find((s) => s.name === selectedLocation.state)) {
  //       let stateData = usStates.find(
  //         (state) => state.name === selectedLocation.state
  //       );
  //       setMapCenter({
  //         lat: Number(stateData.latitude),
  //         lng: Number(stateData.longitude),
  //       });
  //       setMapZoom(4);
  //       // } else if (selectedLocation.type === "region") {
  //       //   setMapCenter(null);
  //       //   setMapZoom(null);
  //       //   let bounds = new window.google.maps.LatLngBounds();
  //       //   Object.values(markers)
  //       //     .map((marker) => ({ lat: Number(marker.lat), lng: Number(marker.lon) }))
  //       //     .forEach((marker) => bounds.extend(marker));
  //       //   map.fitBounds(bounds, 20);
  //       // } else if (selectedLocation.type === "network") {
  //     } else {
  //       setMapCenter(defaultCenter);
  //       setMapZoom(3);
  //     }
  //   } else if (map) {
  //     setMapCenter(defaultCenter);
  //     setMapZoom(3);
  //   }
  // }, [map, previewCenter, previewRadius, selectedLocation]);

  useEffect(() => {
    if (map) {
      setMapCenter(defaultCenter);
      setMapZoom(3);
    }
  }, [map]);

  const handleCameraChange = useCallback((e) => {
    setMapCenter(e.detail.center);
    setMapZoom(e.detail.zoom);
  });

  return (
    <>
      <GoogleMap
        {...mapOptions}
        center={mapCenter}
        className={"metadata-geolocation-map"}
        disableDefaultUI={true}
        gestureHandling={"greedy"}
        mapId={"704f78d978e3652a"}
        onCameraChanged={handleCameraChange}
        reuseMaps={false}
        style={mapStyle}
        zoom={mapZoom}
      >
        <MapControl position={ControlPosition.TOP_CENTER}>
          <UndoRedoControl
            drawingManager={drawingManager}
            handleChangeShapeData={handleChangeShapeData}
          />
        </MapControl>
      </GoogleMap>
    </>
  );
};

export default Map;
